import Web3 from "web3";
import axios from "axios";
import { Contract, loadWasm, Provider } from "necjs";
import { toast, Bounce } from "react-toastify";
import { transactionAction } from "./chain-helper";
import { requireAuth } from "../authMiddleware.js";
import { getCalendarContract } from './contract-helper.js'
import web3Config from "../web3/web3Config.json";
import contactABI from "../web3/contactContract.json";
import { contactDB } from "../db/db-helper.js";
import { getContactsValue, listContactsFromServer, claimContactsOnServer, isApiError } from "../service/api-actions.js";
import { getCachedWalletAccount } from "./wallet-helper.js";
import { encryptText } from "./attachmentHelper.js";
import { reportError, toErrorString } from "../utils/errorLogger.js";
import { getContactsSessionDEK, normalizeDek } from "./contactsDek";

export const web3AccountCheck = async (setWeb3, setAccount) => {
  if (!window.ncogProvider) return;

  const web3Instance = new Web3(web3Config?.CHAIN_DETAILS?.rpcUrls[0]);
  setWeb3(web3Instance);

  try {
    const accountAddress = await getCachedWalletAccount();
    setAccount(accountAddress);
  } catch (error) {
    const userName = localStorage.getItem('UserName');
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: web3AccountCheck, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.log("Error checking web3 account:", error);
  }
};

export const contractCall = (contractABI, contractId, isWss = false) => {
  try {
    const rpcUrl = isWss ? web3Config?.WSS_RPC_URL : web3Config?.CHAIN_DETAILS?.rpcUrls[0];
    const provider = new Provider(rpcUrl);
    const contract = new Contract(contractId, contractABI, provider);
    return contract;
  } catch (error) {
    const userName = localStorage.getItem('UserName');
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: contractCall, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
      level: 'warn',
    });
    console.error("Error getting NEC provider:", error);
    return null;
  }
};

export const getPublicKeyValue = async () => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const accountAddress = await getCachedWalletAccount();
    if (accountAddress) {

      const encryptionPublicKey = await window.ncogProvider.request({
        method: "ncog_encryptionPublicKey"
      });
      return encryptionPublicKey?.response || null;
    }
    return null;
  } catch (error) {
    const userName = localStorage.getItem('UserName');
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: getPublicKeyValue, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
      level: 'warn',
    });
    return null;
  }
};

export const markMessageAsImportant = async (
  contract,
  userName,
  messageObject,
  value,
  account
) => {
  try {
    const functionParams = [
      userName,
      messageObject,
      [{ key: 'isStarred', value: (!value).toString() }],
    ];
    const hash = await transactionAction(
      contract,
      "updateEmailAttributes",
      functionParams,
      account,
      'hostContract'
    );
    if (!hash) return false;
    return true;
  } catch (error) {
    const userName = localStorage.getItem('UserName');
    const errorStr = toErrorString(error || error.message);
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: markMessageAsImportant, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error(error);
    return false;
  }
};

export const getAccountBalance = async (web3, account) => {
  return '0';
  const balance = await web3.eth.getBalance(account);
  return web3.utils.fromWei(balance, "ether");
};

async function getAllEvents(user) {
  try {
    // 1. Get the total number of events
    const contractInstance = getCalendarContract();
    const totalCount = await contractInstance.methods.getEventCount(user).call();

    // Ethers returns a BigNumber, convert it to a regular number for loops
    const totalCountNum = Number(totalCount);

    if (totalCountNum === 0) {
      return []; // Return an empty array immediately
    }

    const allEvents = [];
    const count = 500;
    // 2. Loop until we have fetched all events
    for (let offset = 0; offset < totalCountNum; offset += count) {
      const pageOfEvents = await contractInstance.methods.getEvent(
        user,
        offset,
        count
      ).call();

      allEvents.push(...pageOfEvents);
    }
    return allEvents;

  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: String(error.message || 'Failed to fetch events'),
      error: errorStr,
      userId: userName || '',
      level: 'warn',
    });
    console.error("Failed to fetch events:", error);
    throw error;
  }
}

export const getEvents = async (user, account) => {
  try {
    const result = await getAllEvents(user);
    return result;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: getEvents, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error("Issue:", error);
    return null;
  }
};


export const editEvent = async (functionmethod, functionParams, account, user) => {

  try {
    const contract = getCalendarContract();
    const hash = await transactionAction(contract, functionmethod, functionParams, account, 'calendar')
    return hash;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: editEvent, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error("Issue:", error);
    return null;
  }
}

export const createEvent = async (functionmethod, functionParams, account, user) => {
  try {
    const contract = getCalendarContract();
    const hash = await transactionAction(contract, functionmethod, functionParams, account, 'calendar')
    return hash;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: createEvent, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error("Issue:", error);
    return null;
  }
}

export const deleteEvent = async (eventIndex, account, user) => {
  try {
    const contract = getCalendarContract();
    const params = [eventIndex, user];
    const hash = await transactionAction(
      contract,
      "deleteEvent",
      params,
      account,
      'calendar'
    );
    if (hash) {
      toast.success("Event updated successfully", {
        position: "top-center",
        transition: Bounce,
      });
    } else {
      toast.error("Faild to invite the guests", {
        position: "top-center",
        transition: Bounce,
      });
    }
  } catch (error) {
    console.error(error);
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: deleteEvent, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '-',
    });
    toast.error("Error deleting event", {
      position: "top-center",
      transition: Bounce,
    });
  }
};


export const isEventExist = async (userName, uid, account) => {
  try {
    const result = await getAllEvents(userName);
    const response = result.filter(data => {
      return data.uid === uid
    })
    return response;
  } catch (error) {
    const userName = localStorage.getItem('UserName');
    const errorStr = toErrorString(error || error.message);
    console.log(error, 'error')
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: isEventExist, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    return null;
  }
}
export const getEventCollection = async () => {
  return await contactDB.contacts.get("contactList");
};
const getDecryptedValue = async () => {
  return await contactDB.decryptedValue.get("DecryptedValue");
};

// The legacy chain index: which uuids belong to `user`. It never held a contact — the bodies were
// always in the database — and it is now only a back-fill source for contacts saved before the
// database recorded an owner. Returns every page flattened, since the caller needs the whole uuid
// list before it can ask the server to adopt it.
async function fetchChainContactIndex(user, pageSize) {
  const contractInstance = contractCall(contactABI.contract, web3Config.CONTACT_CONTRACT);
  const totalCount = await contractInstance.methods.getContactsCount(user).call();
  const totalCountNum = Number(totalCount);

  if (totalCountNum === 0) return [];

  const allContacts = [];
  for (let offset = 0; offset < totalCountNum; offset += pageSize) {
    const pageOfContacts = await contractInstance.methods.getEncryptedContacts(
      user,
      offset,
      pageSize
    ).call();
    allContacts.push(...pageOfContacts);
  }
  return allContacts;
}

// Page through the signed-in user's contacts in the database. Yields one page per call.
//
// A FAILED page and an EMPTY page must never collapse to the same value here. fetchAPI does not throw:
// it returns the {__error:true} sentinel (see isApiError in service/api-actions.js), and reading that as
// "no rows" made every failure look like the end of the address book — one transient 500 or 429 on page
// 3 of 9 returned 200 of 848 contacts with no error, and getContacts then wrote that partial set into
// IndexedDB as if it were complete. The chain read this replaced could not do that: a failed page threw
// and the caller's catch fired. Throwing restores that, and only a real 200 with an empty `value` ends
// the loop. A response that is not the sentinel but has no `value` array is equally unusable, so it
// takes the same path rather than being silently rounded down to zero rows.
async function* listOwnedContacts(batchSize) {
  let offset = 0;
  while (true) {
    const page = await listContactsFromServer(offset, batchSize);
    if (isApiError(page) || !Array.isArray(page?.value)) {
      throw new Error(
        `Contact listing failed at offset ${offset}: ${page?.message || 'unexpected response'}`
      );
    }
    const rows = page.value;
    if (rows.length === 0) return;

    yield rows;

    offset += rows.length;
    // `total` is the server's own count for this user, so believe it over the size of one page: a short
    // page while rows remain is a truncated response, not the end of the list.
    const total = Number(page?.total);
    if (Number.isFinite(total)) {
      if (offset >= total) return;
      continue;
    }
    if (rows.length < batchSize) return;
  }
}

// One back-fill attempt per user per session — see reconcileChainOwnership below.
const chainOwnershipReconciled = new Set();

// Owning SOME rows is not evidence of owning ALL of them. dContacts writes contacts to the database with
// an owner, so a user can end up with a handful of owned rows while their pre-migration contacts still
// have no owner at all; gating the back-fill on "the listing came back completely empty" then skips it
// forever, and the address book quietly renders 1 contact instead of 849 with no error and no way back.
// So reconcile the listed count against the chain's own count and adopt the difference.
//
// Bounded deliberately: at most one attempt per user per session (a mismatch that survives the claim
// must not turn every contacts read into another claim), and if the chain count cannot be read the
// answer is to do nothing — an unknown count is not permission to keep trying.
async function* reconcileChainOwnership(user, listedCount, seenUuids, batchSize) {
  if (chainOwnershipReconciled.has(user)) return;
  // Marked before the work, not after: a failed attempt must not re-arm itself on the next read.
  chainOwnershipReconciled.add(user);

  let missing = [];
  try {
    const contractInstance = contractCall(contactABI.contract, web3Config.CONTACT_CONTRACT);
    const chainCount = Number(await contractInstance.methods.getContactsCount(user).call());
    if (!Number.isFinite(chainCount) || chainCount <= listedCount) return;

    const chainContacts = await fetchChainContactIndex(user, batchSize);
    const uuids = chainContacts.map(row => row?.uuid).filter(Boolean);
    if (uuids.length === 0) return;

    const claimed = await claimContactsOnServer(uuids);
    if (isApiError(claimed)) {
      console.warn("Could not adopt chain-indexed contacts:", claimed?.message);
      return;
    }
    // Nothing actually changed owner (the rows are someone else's, or already gone), so there is
    // nothing extra to show. Rows that DID move are safe to leave to the next listing anyway.
    if (!(Number(claimed?.claimed) > 0)) return;

    // Yield only what the listing did not already return, so this read shows the whole address book
    // instead of healing on some later load — without handing the same uuid to the decrypt path twice.
    missing = chainContacts.filter(row => row?.uuid && !seenUuids.has(row.uuid));
  } catch (reconcileError) {
    // Fail safe: the listing above already succeeded, so a chain or claim failure costs a back-fill,
    // never the read itself.
    console.warn("Contact ownership back-fill skipped:", reconcileError?.message);
    return;
  }

  for (let offset = 0; offset < missing.length; offset += batchSize) {
    yield missing.slice(offset, offset + batchSize);
  }
}

async function* fetchAllContacts(user, batchSize = 100) {
  try {
    // The chain read this replaced (getContactsCount + getEncryptedContacts) was never fetching
    // contacts, only ownership: `dcontact` had no column saying whose row was whose, so that one fact
    // lived on chain. It has a column now, and contacts saved since then are not written to the chain
    // at all — so an app still reading the chain index shows an address book missing everything added
    // since the migration.
    let listed = 0;
    const seenUuids = new Set();
    for await (const rows of listOwnedContacts(batchSize)) {
      listed += rows.length;
      for (const row of rows) {
        if (row?.uuid) seenUuids.add(row.uuid);
      }
      yield rows;
    }
    if (listed > 0) {
      yield* reconcileChainOwnership(user, listed, seenUuids, batchSize);
      return;
    }

    // Reaching here means a REAL 200 with an empty page — the listing throws on failure, so this can no
    // longer be a dropped request. That distinction is what keeps the block below off the wire during a
    // 429: firing a chain read plus a claim because the request that failed "returned nothing" added
    // load in exactly the rate-limited condition that caused it.
    // Nothing owned in the database — this user's contacts predate the owner column, so read the chain
    // index once and hand it over to be adopted.
    const chainContacts = await fetchChainContactIndex(user, batchSize);
    if (chainContacts.length === 0) {
      console.log("No contacts found for this user.");
      return;
    }

    const uuids = chainContacts.map(row => row?.uuid).filter(Boolean);
    let adopted = 0;
    if (uuids.length > 0) {
      try {
        const claimed = await claimContactsOnServer(uuids);
        if (!isApiError(claimed)) {
          for await (const rows of listOwnedContacts(batchSize)) {
            adopted += rows.length;
            yield rows;
          }
          if (adopted > 0) return;
        }
      } catch (claimError) {
        // Non-fatal: the chain rows below are still a truthful answer for this read, so a failed
        // back-fill costs a slower next load rather than an empty address book.
        //
        // Only while nothing has been yielded yet, though. Once the re-listing has handed out rows, a
        // failure part-way through leaves an incomplete answer that the chain dump below cannot repair —
        // it would re-yield the same uuids and re-fetch their bodies during the failure. Better to fail
        // the read than to top up a truncated address book.
        if (adopted > 0) throw claimError;
        console.warn("Could not adopt chain-indexed contacts:", claimError);
      }
    }

    // The claim adopted nothing (or could not run) — yield what the chain knows rather than leave the
    // user with an empty address book.
    for (let offset = 0; offset < chainContacts.length; offset += batchSize) {
      yield chainContacts.slice(offset, offset + batchSize);
    }

  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: String(`src\web3Contract\Web3Helper.js - ${error.message} - FUN: fetchAllContacts` || 'Error not handle'),
      error: errorStr,
      userId: userName || '',
    });
    console.error("Failed to fetch contacts:", error);
    throw error;
  }
}

export const getContacts = async (userName, symmetricKey = null) => {
  try {
    if (!userName) return;
    // Resolve the dContacts DEK here rather than making every caller thread it — Profile.js and
    // WalletConnect.js called this with no key, so their v2 (DEK-encrypted) contacts were skipped.
    // Also normalise a caller-supplied key: the wallet NUL-pads the unwrapped value and dContacts
    // strips that padding, so an unstripped key would decrypt to garbage.
    if (symmetricKey) {
      symmetricKey = normalizeDek(symmetricKey);
    } else {
      try {
        symmetricKey = await getContactsSessionDEK(userName);
      } catch (dekError) {
        console.error("Could not resolve the contacts DEK:", dekError?.message);
        symmetricKey = null;
      }
    }
    const web3 = new Web3(web3Config?.CHAIN_DETAILS?.rpcUrls[0]);
    const contractInstance = contractCall(contactABI.contract, web3Config.CONTACT_CONTRACT);
    const contractValue = contractInstance;

    let decryptedUUIDValue = [];
    let allEvents = [];

    const [eventCollection, decryptedValue] = await Promise.all([
      getEventCollection(),
      getDecryptedValue(),
    ]);

    const data = eventCollection?.data || [];
    const decryptedData = decryptedValue?.data || [];
    let uniqueuuid = [];
    allEvents.push(...data);
    decryptedUUIDValue.push(...decryptedData.map(d => ({ decryptedUUID: d.decryptedUUID })));
    if (symmetricKey) {
      // 🔁 BATCH LOOP START
      for await (const batch of fetchAllContacts(userName, 100)) {

        const getContactValue = [];
        const encryptedData = [];

        for (const element of batch) {
          if (!element?.uuid) continue;

          const alreadyDecrypted = decryptedUUIDValue.some(
            item => item.decryptedUUID === element.uuid
          );

          if (!alreadyDecrypted) {
            getContactValue.push(element.uuid);
          }
        }

        if (getContactValue.length > 0) {
          const resResponse = await getContactsValue(getContactValue);
          if (resResponse?.value?.length > 0) {
            resResponse.value.forEach(data => {
              // Carry the uuid on the row so it is only marked decrypted once it actually
              // succeeds. Marking up-front (the old behaviour) permanently skipped any row that
              // failed to decrypt, so a bad row stayed invisible forever.
              encryptedData.push({
                uuid: data?.uuid,
                encryptedData: data?.contact_encrypted_message,
                version: data?.version || "v1"
              });
            });
          }
        }
        let decryptedResponse;
        let pendingUuids = [];

        if (encryptedData.length > 0) {
          // Split per ROW version. dContacts writes version 'v2' for DEK-encrypted rows: those
          // decrypt locally with the shared contacts DEK and never touch the wallet. Everything
          // else is legacy wallet-encrypted and goes through ONE bulkDecrypt batch. Both shapes
          // coexist in one load — branching on "does a DEK exist" instead of per-row (the old
          // behaviour) handed v2 rows the wrong version, so every DEK contact threw and vanished.
          const v2Rows = [];
          const legacyRows = [];
          for (const row of encryptedData) {
            (row.version === "v2" ? v2Rows : legacyRows).push(row);
          }

          const plaintexts = [];

          if (v2Rows.length > 0 && symmetricKey) {
            const MLKEM = await loadWasm();
            for (const row of v2Rows) {
              try {
                // 'v1' is REQUIRED by the WASM layer — 'v2' is only a row discriminator, never a
                // crypto version. Passing the row version here is what made every v2 contact throw.
                const result = await MLKEM.symDecrypt(symmetricKey, row.encryptedData, "v1");
                if (result) {
                  plaintexts.push(result);
                  pendingUuids.push(row.uuid);
                }
              } catch (decryptError) {
                console.error(`Failed to decrypt v2 contact ${row.uuid}:`, decryptError?.message);
              }
            }
          } else if (v2Rows.length > 0) {
            console.error(`Skipping ${v2Rows.length} v2 contact(s): no contacts DEK available`);
          }

          if (legacyRows.length > 0) {
            const bulkResponse = await window.ncogProvider.request({
              method: "ncog_bulkDecrypt",
              params: {
                userMessage: legacyRows.map(r => ({
                  encryptedData: r.encryptedData,
                  version: r.version
                }))
              },
            });
            // Index back into legacyRows only — the payload was built from that same filtered
            // array, so the indices line up.
            (bulkResponse?.response || []).forEach((res, i) => {
              if (res) {
                plaintexts.push(res);
                pendingUuids.push(legacyRows[i]?.uuid);
              }
            });
          }

          decryptedResponse = { response: plaintexts };
          if (decryptedResponse?.response?.length > 0) {
            decryptedResponse.response.forEach((res, idx) => {
              try {
                const cleanedString = res.replace(/\u0000/g, "");
                const jsonObject = JSON.parse(cleanedString);
                // Handle both array and single object responses
                if (Array.isArray(jsonObject)) {
                  allEvents.push(...jsonObject);
                } else if (jsonObject) {
                  allEvents.push(jsonObject);
                }
                // Mark decrypted ONLY once the plaintext actually parsed, so a row that fails is
                // retried next load instead of being permanently skipped.
                const okUuid = pendingUuids[idx];
                if (okUuid) decryptedUUIDValue.push({ decryptedUUID: okUuid });
              } catch (err) {
                console.error("Invalid JSON for contact:", err);
              }
            });
          }
        }

        // 💾 Save progressively after each batch
        const currentUniqueUUIDs = decryptedUUIDValue.reduce((acc, current) => {
          if (!acc.some(event => event.decryptedUUID === current.decryptedUUID)) {
            acc.push(current);
          }
          return acc;
        }, []);

        try {
          // ORDER IS LOAD-BEARING: contacts FIRST, the decrypted-markers LAST.
          //
          // The marker table is a "do not decrypt this uuid again" list. Writing it before the data it
          // describes means a failure of the second put leaves markers for contacts whose content was
          // never stored: the next load skips them as already-decrypted, so they are invisible forever —
          // and this catch swallows the error, so nothing ever retries. Writing the data first makes a
          // partial failure self-healing: worst case the markers are missing, the contacts are
          // re-decrypted on the next load, and re-decryption is idempotent.
          await contactDB.contacts.put({ id: "contactList", data: allEvents });
          await contactDB.decryptedValue.put({ id: "DecryptedValue", data: currentUniqueUUIDs });
          console.log(`📊 Saved ${allEvents.length} contacts to IndexedDB`);
        } catch (dbError) {
          console.error("Database save error in batch:", dbError);
        }
      }

      uniqueuuid = decryptedUUIDValue.reduce((acc, current) => {
        if (!acc.some(event => event.decryptedUUID === current.decryptedUUID)) {
          acc.push(current);
        }
        return acc;
      }, []);
      // 🔁 BATCH LOOP END
    } else {
      const getContactValue = [];
      // This branch had its own copy of the chain index read. It uses the same database-first read as
      // the DEK branch above (see fetchAllContacts) — the chain only ever answered "which uuids are
      // this user's", and it no longer knows about contacts saved after the owner column landed. Only
      // the uuids are used here; the bodies are fetched and wallet-decrypted below exactly as before.
      const response = [];
      for await (const batch of fetchAllContacts(userName, 1000)) {
        response.push(...batch);
      }
      let encryptedData = [];
      for (const element of response) {
        // Optional validation: Check if element has a uuid property
        if (!element.uuid) {
          console.warn("Element missing uuid property, skipping...");
          continue;
        }

        const alreadyDecrypted = decryptedData.some(item => item.decryptedUUID === element.uuid);
        if (!alreadyDecrypted) {
          getContactValue.push(element.uuid);
        }
      }
      if (getContactValue?.length > 0) {
        const resValue = await getContactsValue(getContactValue);
        const resResponse = resValue;
        if (resResponse?.value?.length > 0) {
          resResponse.value.map(data => {
            encryptedData.push({ encryptedData: data?.contact_encrypted_message, version: data?.version || "v1" });
            decryptedUUIDValue.push({ decryptedUUID: data.uuid })
          })
        }
      }
      uniqueuuid = decryptedUUIDValue.reduce((acc, current) => {
        if (!acc.some(event => event.decryptedUUID === current.decryptedUUID)) {
          acc.push(current);
        }
        return acc;
      }, []);

      let decryptedResponse;
      if (encryptedData.length > 0) {
        decryptedResponse = await window.ncogProvider.request({
          method: "ncog_bulkDecrypt",
          params: { userMessage: encryptedData },
        });
        // Optional validation: Check if decryptedResponse is valid
        if (decryptedResponse?.response?.length > 0) {
          decryptedResponse.response.map(res => {
            const cleanedString = res.replace(/\u0000/g, "");
            try {
              const jsonObject = JSON.parse(cleanedString);
              if (jsonObject) {
                allEvents.push(jsonObject);
              }
            } catch (err) {
              const errorStr = toErrorString(err || err.message);
              const userName = localStorage.getItem('UserName');
              reportError({
                endpoint: '-',
                message: String(`src\web3Contract\Web3Helper.js - ${err.message} - FUN: getContacts` || 'Error not handle'),
                error: errorStr,
                userId: userName || '',
              });
              console.error("Invalid JSON:", err);
            }
          });
        }

      }
    }
    const formattedContactsList = Array.from(
      new Map(
        allEvents
          .filter(contact =>
            !contact.attributes?.some(attr =>
              (attr.key === 'isDeleted' && attr.value === 'true') ||
              (attr.key === 'isPermanentlyDeleted' && attr.value === 'true')
            )
          )
          .map(value => [value.contactId, value])
      ).values()
    );

    // Same ordering rule as the batch save above: contacts FIRST, markers LAST. The outer catch logs and
    // returns, so a throw between the two writes is silent — it must not be able to leave a uuid marked
    // decrypted with no stored content, which would hide that contact permanently.
    await contactDB.contacts.put({ id: "contactList", data: allEvents });
    await contactDB.decryptedValue.put({ id: "DecryptedValue", data: uniqueuuid });

    const labels = await contractValue.methods.getLabels(userName).call();

    return { formattedContactsList, labels };

  } catch (err) {
    const errorStr = toErrorString(err || err.message);
    const userNameLocal = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `src\\web3Contract\\Web3Helper.js - ${err.message} - FUN: getContacts`,
      error: errorStr,
      userId: userNameLocal || '',
    });
    console.error("Error fetching contacts:", err);
  }
};

export const updateEvent = async (token, shareCalendarparams) => {
  try {

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await axios.post(
      `${web3Config.EMAIL_API}/updateevents`,
      shareCalendarparams,
      config
    );

    return response.data;
  } catch (error) {
    const errorStr = toErrorString(error || error.message);
    const userName = localStorage.getItem('UserName');
    reportError({
      endpoint: '-',
      message: `FileName: Web3helper.js, Fun: updateEvent, ${String(error.message || 'Unhandled error')}`,
      error: errorStr,
      userName: userName || '',
    });
    console.error("Error updating event:", error.response?.data || error.message);
    throw error;
  }
};

export const encrypt = async (msg, token, uid) => {
  const encryptionPublicKey = await window.ncogProvider.request({
    method: "ncog_encryptionPublicKey",
  });
  let config = {
    headers: {
      "content-type": "application/json",
      'Authorization': 'Bearer ' + token,
    },
  }
  const publicKey = encryptionPublicKey.response;
  const msgToEncrypt = Array.isArray(msg) ? msg : [msg];
  const encryptionPromises = msgToEncrypt.map(async (data) => {
    const msgString = JSON.stringify(data);
    const result = await encryptText(msgString, publicKey);
    return result;
  });
  const encryptedMessages = await Promise.all(encryptionPromises);
  const requestData = {
    msg: encryptedMessages,
    uid: uid
  };

  const result = await axios.post(`${web3Config.EMAIL_API}/getEncryptCalendarValue`, requestData, config)
  return result;
}
